import React, { useState } from 'react';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import DnsIcon from '@mui/icons-material/Dns';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CodeSnippet from './CodeSnippet';

export interface SnippetGuide {
    title: string;
    steps: string[];
    note?: string;
}

/** One file within a snippet — its own path header + copy button. */
export interface SnippetFile {
    title: string;
    code: string;
}

export interface SnippetDef {
    /** Single-file snippet. Ignored when `files` is provided. */
    title?: string;
    code?: string;
    /**
     * Multi-file snippet — when the integration spans more than one file
     * (e.g. ClanConnectPixel.tsx + App.tsx + global.d.ts), each file renders
     * as its own code block with its path shown at the top, for clarity.
     */
    files?: SnippetFile[];
    guide?: SnippetGuide;
}

/** One selectable platform/framework tab (HTML, WordPress, Next.js, PHP, …) */
export interface SnippetOption {
    id: string;
    label: string;
    /** Picks the tab icon: web = globe, mobile = phone, server = server */
    group: 'web' | 'mobile' | 'server';
    snippet: SnippetDef;
}

interface Props {
    options: SnippetOption[];
}

const GROUP_ICON: Record<SnippetOption['group'], React.ReactElement> = {
    web: <LanguageIcon fontSize="small" />,
    mobile: <PhoneIphoneIcon fontSize="small" />,
    server: <DnsIcon fontSize="small" />,
};

/**
 * Framework/platform picker for integration code — flat scrollable tab list
 * (HTML | WordPress | Shopify | … | PHP | REST API), the way most API docs do it.
 * Code renders on the left, a per-platform integration guide on the right;
 * the two columns stack below the md breakpoint.
 */
const PlatformSnippetTabs: React.FC<Props> = ({ options }) => {
    const [active, setActive] = useState(0);
    // Which file's Copy button is in its "Copied" state — keyed per file so
    // multi-file snippets track each block independently.
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    if (!options || options.length === 0) return null;

    const current = options[Math.min(active, options.length - 1)];
    const snippet = current.snippet;

    // Normalise to a file list — single-file snippets become a one-item list.
    const files: SnippetFile[] = snippet.files?.length
        ? snippet.files
        : [{ title: snippet.title || '', code: snippet.code || '' }];

    const handleCopy = (key: string, code: string) => {
        navigator.clipboard.writeText(code);
        setCopiedKey(key);
        setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 2500);
    };

    return (
        <Box>
            {options.length > 1 && (
                <Tabs
                    value={Math.min(active, options.length - 1)}
                    onChange={(_, v) => {
                        setActive(v);
                        setCopiedKey(null);
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                        mb: 1.5,
                        minHeight: 40,
                        borderBottom: '1px solid #E2E8F0',
                        '& .MuiTab-root': {
                            minHeight: 40,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 1.5,
                        },
                    }}
                >
                    {options.map((opt) => (
                        <Tab
                            key={opt.id}
                            icon={GROUP_ICON[opt.group]}
                            iconPosition="start"
                            label={opt.label}
                        />
                    ))}
                </Tabs>
            )}

            {/* Code left, integration guide right — stacks on small screens */}
            <Box
                sx={{
                    display: 'grid',
                    gap: 2,
                    alignItems: 'start',
                    gridTemplateColumns: {
                        xs: '1fr',
                        md: snippet.guide ? 'minmax(0, 1.65fr) minmax(0, 1fr)' : '1fr',
                    },
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    {files.map((file, i) => {
                        const key = `${current.id}-${i}`;
                        return (
                            <CodeSnippet
                                key={key}
                                title={file.title}
                                code={file.code}
                                copied={copiedKey === key}
                                onCopy={() => handleCopy(key, file.code)}
                            />
                        );
                    })}
                </Box>

                {snippet.guide && (
                    <Box
                        sx={{
                            p: 2.5,
                            borderRadius: 4,
                            bgcolor: '#F8FAFC',
                            border: '1px solid #E2E8F0',
                            position: { md: 'sticky' },
                            top: { md: 16 },
                            // Long unbroken tokens (URLs, query strings) must wrap
                            // inside the card instead of overflowing it
                            minWidth: 0,
                            overflowWrap: 'anywhere',
                        }}
                    >
                        <Typography fontWeight={700} mb={1.5}>
                            {snippet.guide.title}
                        </Typography>

                        <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
                            {snippet.guide.steps.map((step, i) => (
                                <Typography
                                    key={i}
                                    component="li"
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 1, lineHeight: 1.6, wordBreak: 'break-word' }}
                                >
                                    {step}
                                </Typography>
                            ))}
                        </Box>

                        {snippet.guide.note && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    mt: 1.5,
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: '#EFF6FF',
                                    border: '1px solid #BFDBFE',
                                }}
                            >
                                <InfoOutlinedIcon sx={{ color: '#2563EB', fontSize: 18, mt: '1px' }} />
                                <Typography variant="caption" color="#1E40AF" sx={{ lineHeight: 1.5 }}>
                                    {snippet.guide.note}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default PlatformSnippetTabs;
